import { Routes } from '@angular/router';
import { EnumRouteName } from '@lyricstream/shared';
import { langGuard } from './core/guards/lang.guard';
import { langResolver } from './core/resolvers/lang.resolver';
import { lyricSeoResolver } from './core/resolvers/lyric-seo.resolver';
import { namedPageSeoResolver, pageSeoResolver, recueilSeoResolver } from './core/resolvers/page-seo.resolver';

export const SUPPORTED_LANGS = ['fr', 'ht', 'en', 'es'] as const;

/**
 * Routes enfants partagées par /recueils et /groupe-ce : même pages (liste + détail),
 * deux préfixes d'URL. Les composants lisent le préfixe actif via ActivatedRoute.
 */
export const recueilRoute = (): Routes => [
  {
    path: '',
    pathMatch: 'full',
    resolve: { seo: pageSeoResolver('RECUEILS') },
    loadComponent: () => import('./features/recueils/recueils').then((m) => m.RecueilsComponent),
  },
  {
    path: ':id',
    resolve: { seo: recueilSeoResolver },
    loadComponent: () => import('./features/recueil/recueil').then((m) => m.RecueilComponent),
  },
];

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'fr' },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/callback/callback').then((m) => m.CallbackComponent),
  },
  {
    // Lien de partage sans préfixe de langue (cf. ShareMenuComponent/PlaylistCardComponent
    // shareUrl, déjà générés sous cette forme) — même repli statique 'fr' que la route racine
    // ci-dessus, en attendant une vraie détection de langue.
    path: `${EnumRouteName.Playlists}/:id`,
    redirectTo: ({ params }) => `fr/${EnumRouteName.Playlists}/${params['id']}`,
  },
  {
    path: ':lang',
    canActivate: [langGuard],
    resolve: { lang: langResolver },
    children: [
      {
        path: '',
        pathMatch: 'full',
        resolve: { seo: pageSeoResolver('HOME') },
        loadComponent: () => import('./features/home/home').then((m) => m.HomeComponent),
      },
      {
        path: EnumRouteName.Discover,
        resolve: { seo: pageSeoResolver('DISCOVER') },
        loadComponent: () =>
          import('./features/discover/discover').then((m) => m.DiscoverComponent),
      },
      {
        path: 'decouvrir/:slug',
        redirectTo: ({ params }) => `lyric/${params['slug']}`,
      },
      {
        path: `${EnumRouteName.LyricViewPublic}1/:id`,
        loadComponent: () => import('./features/lyric/lyric').then((m) => m.LyricComponent),
      },
      {
        path: `${EnumRouteName.LyricViewPublic}/:id`,
        resolve: { seo: lyricSeoResolver },
        loadComponent: () =>
          import('./features/lyric-reader/lyric-reader').then((m) => m.LyricReaderComponent),
      },
      {
        path: `${EnumRouteName.LyricViewPublic}/:id/karaoke`,
        loadComponent: () => import('./features/karaoke/karaoke').then((m) => m.KaraokeComponent),
      },
      {
        path: 'lyric/:id/project',
        loadComponent: () => import('./features/project/project').then((m) => m.ProjectComponent),
      },
      {
        path: EnumRouteName.GetStarted,
        loadComponent: () =>
          import('./features/onboarding/onboarding').then((m) => m.OnboardingComponent),
      },
      {
        path: 'premium',
        resolve: { seo: pageSeoResolver('PREMIUM') },
        loadComponent: () => import('./features/premium/premium').then((m) => m.PremiumComponent),
      },
      {
        path: EnumRouteName.Recueils,
        children: recueilRoute(),
      },
      {
        path: EnumRouteName.GroupeCE,
        children: recueilRoute(),
      },
      {
        path: EnumRouteName.Conditions,
        resolve: { seo: pageSeoResolver('CONDITIONS') },
        loadComponent: () =>
          import('./features/legal/conditions/conditions').then((m) => m.ConditionsComponent),
      },
      {
        path: EnumRouteName.Confidentialite,
        resolve: { seo: pageSeoResolver('PRIVACY') },
        loadComponent: () =>
          import('./features/legal/privacy/privacy').then((m) => m.PrivacyComponent),
      },
      {
        path: 'contributeurs/:handle',
        resolve: { seo: namedPageSeoResolver('CONTRIBUTEUR', 'handle') },
        loadComponent: () =>
          import('./features/contributeur/contributeur').then((m) => m.ContributeurComponent),
      },
      {
        path: EnumRouteName.Popular,
        resolve: { seo: pageSeoResolver('POPULAR') },
        loadComponent: () => import('./features/popular/popular').then((m) => m.PopularComponent),
      },
      {
        path: EnumRouteName.Recents,
        resolve: { seo: pageSeoResolver('RECENTS') },
        loadComponent: () => import('./features/latest/latest').then((m) => m.LatestComponent),
      },
      {
        path: EnumRouteName.Search,
        resolve: { seo: pageSeoResolver('SEARCH') },
        loadComponent: () =>
          import('./features/search/search').then((m) => m.SearchComponent),
      },
      {
        // Page playlist publique — réutilise le même composant que /admin/my-playlists/:id
        // (l'accès public/privé+partagé/propriétaire est déjà tranché côté CoreService,
        // cf. GetPlaylistByIdAsync), mais SANS authGuard : un visiteur anonyme doit pouvoir
        // consulter une playlist publique. `publicView` distingue ce contexte pour les liens
        // retour (Découvrir/Accueil plutôt que le dashboard admin) et le tracking de visite.
        path: `${EnumRouteName.Playlists}/:id`,
        data: { publicView: true },
        resolve: { seo: pageSeoResolver('PLAYLIST', false) },
        loadComponent: () =>
          import('./features/admin/my-playlists/playlist-detail/playlist-detail').then(
            (m) => m.PlaylistDetailComponent,
          ),
      },
      {
        // Page album publique : seul un album publié avec au moins un chant visible répond (404 sinon).
        path: `${EnumRouteName.Albums}/:id`,
        resolve: { seo: pageSeoResolver('ALBUM', false) },
        loadComponent: () => import('./features/album/album').then((m) => m.AlbumComponent),
      },
      {
        path: EnumRouteName.Admin,
        loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
      },
      {
        path: '',
        loadChildren: () => import('./features/features.routes').then((m) => m.FEATURES_ROUTES),
      },
      { path: '**', redirectTo: '' },
    ],
  },
  { path: '**', redirectTo: 'fr' },
];
